import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { useTaxRateSuggestion } from '@/lib/services/organization-tax-settings/organization-tax-settings.query'
import { taxRatePercentage } from '@/lib/services/organization-tax-settings/organization-tax-settings.schema'

export function TaxPolicyFields({
  form,
  organizationId,
  location,
  inheritedPolicy,
  prefix = '',
  deliveryModes,
  isReadOnly,
}) {
  const modePath = `${prefix}calculation_mode`
  const ratePath = `${prefix}fixed_tax_rate`
  const suggestion = useTaxRateSuggestion({ organizationId, locationId: location?.id })
  const chooseMode = async (mode) => {
    if (mode !== 'fixed' || form.getFieldValue(ratePath) !== '' || isReadOnly) return
    const result = await suggestion.refetch()
    // A late suggestion cannot replace a manual entry or apply to a different mode.
    if (
      result.data?.rate != null &&
      form.getFieldValue(modePath) === 'fixed' &&
      form.getFieldValue(ratePath) === '' &&
      !form.getFieldMeta(ratePath)?.isDirty
    ) {
      form.setFieldValue(ratePath, taxRatePercentage(result.data.rate))
    }
  }

  return (
    <div className="space-y-4">
      <form.AppField name={modePath}>
        {(field) => (
          <field.RadioButtonGroup
            label="How should taxes be calculated?"
            options={[
              ...(location ? [{ id: 'inherit', name: 'Same as all locations' }] : []),
              { id: 'automatic', name: 'Automatic' },
              { id: 'fixed', name: 'Fixed rate' },
            ]}
            appearance="segmented"
            orientation="responsive"
            classNames={{ container: '@md/segmented-control:w-fit' }}
            disabled={isReadOnly}
            onValueChange={chooseMode}
          />
        )}
      </form.AppField>
      <form.Subscribe selector={() => form.getFieldValue(modePath)}>
        {(mode) => {
          if (mode === 'inherit') {
            return (
              <Typography variant="supporting">
                {inheritedPolicy.calculation_mode === 'fixed'
                  ? `Using the rate set for all locations: ${inheritedPolicy.fixed_tax_rate}%.`
                  : 'Using the automatic tax settings set for all locations.'}
              </Typography>
            )
          }
          if (mode === 'fixed') {
            return (
              <div className="space-y-3">
                <div className="max-w-xs">
                  <form.AppField name={ratePath}>
                    {(field) => (
                      <field.PercentInput
                        label="Fixed tax rate"
                        decimalPlaces={4}
                        clampOnBlur={false}
                        placeholder="Enter a rate"
                        disabled={isReadOnly}
                        aria-invalid={field.state.meta.errors.length > 0}
                      />
                    )}
                  </form.AppField>
                </div>
                {suggestion.isFetching ? (
                  <Typography variant="supporting">Looking up this location’s rate…</Typography>
                ) : null}
                {suggestion.data?.rate != null ? (
                  <Typography variant="supporting">
                    Suggested rate for {suggestion.data.location_name}:{' '}
                    {taxRatePercentage(suggestion.data.rate)}%.
                  </Typography>
                ) : suggestion.data?.message || suggestion.isError ? (
                  <Typography variant="supporting">
                    {suggestion.data?.message ??
                      'We couldn’t look up a suggested rate. Enter your tax rate to continue.'}
                  </Typography>
                ) : null}
                {!isReadOnly && !suggestion.isFetching ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => suggestion.refetch()}
                  >
                    Look up local rate
                  </Button>
                ) : null}
                <form.AppField name={`${prefix}delivery_tax_mode`}>
                  {(field) => (
                    <field.RadioButtonGroup
                      label="Tax delivery fees?"
                      options={[
                        { id: 'taxable', name: 'Yes' },
                        { id: 'non_taxable', name: 'No' },
                      ]}
                      // Existing fixed settings with Automatic have always taxed delivery.
                      displayValue={field.state.value === 'non_taxable' ? 'non_taxable' : 'taxable'}
                      appearance="segmented"
                      orientation="responsive"
                      classNames={{ container: '@md/segmented-control:w-fit' }}
                      disabled={isReadOnly}
                    />
                  )}
                </form.AppField>
              </div>
            )
          }
          return (
            <form.AppField name={`${prefix}delivery_tax_mode`}>
              {(field) => (
                <div className="space-y-3">
                  <field.RadioButtonGroup
                    label="How should delivery fees be taxed?"
                    options={deliveryModes.map((option) => ({
                      id: option.value,
                      name: option.label,
                      ariaLabel:
                        option.value === 'automatic' ? 'Automatic (recommended)' : option.label,
                    }))}
                    appearance="segmented"
                    orientation="responsive"
                    classNames={{ container: '@md/segmented-control:w-fit' }}
                    disabled={isReadOnly}
                  />
                  {field.state.value === 'automatic' ? (
                    <Typography variant="supporting" className="block">
                      We’ll calculate delivery fee tax automatically based on the delivery address.
                    </Typography>
                  ) : null}
                </div>
              )}
            </form.AppField>
          )
        }}
      </form.Subscribe>
    </div>
  )
}
